import { Box, SvgIcon } from '@mui/material';
import SignRequestIcon from 'assets/icons/sign-request.svg';
import { any, bool, func, node, number, string } from 'prop-types';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import 'react-pdf/dist/Page/TextLayer.css';
import useDimensions from 'react-cool-dimensions';
import Draggable from 'react-draggable';
import { I18n } from 'utils/i18n';
import getFile from 'utils/getFile';
import getPdfjs from 'utils/pdfjs';

const Document = React.lazy(() => import('./Document'));
const Page = React.lazy(() => import('./Page'));

const pageWrapperSx = {
  flex: 1,
  width: '100%',
  position: 'relative',
};

const annotationsSx = {
  position: 'absolute',
  zIndex: 10,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  margin: '20px',
};

const textLayerSx = {
  position: 'absolute',
  textAlign: 'initial',
  inset: 0,
  overflow: 'hidden',
  lineHeight: 1,
  zIndex: 2,
  transformOrigin: '0 0',
  '& span': {
    color: 'transparent',
    position: 'absolute',
    whiteSpace: 'pre',
    cursor: 'text',
    margin: 0,
    transformOrigin: '0 0',
  },
  '& span.markedContent': {
    top: 0,
    height: 0,
  },
};

const boxSx = {
  height: `${(4 / 7) * 100}%`,
  display: 'flex',
  backgroundColor: 'rgba(230, 134, 0, 0.1)',
  color: '#e68600',
  fontSize: 13,
  borderRadius: '4px',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e68600',
};

const iconSx = {
  fontSize: 13,
  marginRight: '0.5em',
  marginLeft: '-0.5em',
};

const signTextSx = {
  height: `${(3 / 7) * 100}%`,
  fontSize: 7,
};

const getDraggableSx = ({ top, left, width, height, display, dragging }) => ({
  cursor: dragging ? 'grabbing' : 'grab',
  position: 'absolute',
  flexDirection: 'column',
  ...(top != null ? { top: `${top}px` } : {}),
  ...(left != null ? { left: `${left}px` } : {}),
  width,
  height,
  display,
});

const PDFPreviewLazy = ({
  file,
  docClassName,
  pageClassName,
  width = null,
  loading,
  showSignature = false,
  onSignaturePlaced = () => {},
  canvasRef = null,
  onScaleChange = () => {},
  fullWidth = false,
  pageNum = 1,
}) => {
  const canvas = useRef();
  const textLayerDiv = useRef();
  const onScaleChangeRef = useRef(onScaleChange);
  onScaleChangeRef.current = onScaleChange;
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dimensions, setDimensions] = useState(null);

  const { observe, width: containerWidth } = useDimensions();
  const pageWidth = width || fullWidth ? containerWidth - 40 : undefined;

  const draggableProps = { display: 'none', dragging };
  if (dimensions) {
    const scale = dimensions.width / dimensions.originalWidth;
    draggableProps.scale = scale;
    draggableProps.left = (dimensions.originalWidth - 5 * 40 + 30) * scale;
    draggableProps.top = (dimensions.originalHeight - 110) * scale;
    draggableProps.height = 70 * scale;
    draggableProps.width = 141 * scale;
    draggableProps.display = 'flex';
  }

  const loadPdf = () => {
    setLoaded(true);
  };

  const loadPage = p => {
    setPage(p);
    setDimensions({
      width: p.width,
      height: p.height,
      originalWidth: p.originalWidth,
      originalHeight: p.originalHeight,
    });
  };

  useEffect(() => {
    let cancelled = false;
    let textLayerTask = null;

    (async () => {
      if (!(page?.originalWidth > 0)) {
        return;
      }
      const viewport = page.getViewport({
        scale: pageWidth ? pageWidth / page.originalWidth : 1,
        rotation: page.rotate,
      });
      const pdfjs = await getPdfjs();
      if (cancelled || !textLayerDiv.current) {
        return;
      }
      textLayerDiv.current.innerHTML = '';

      if (cancelled || !textLayerDiv.current) {
        return;
      }
      const textContentSource = page.streamTextContent({ includeMarkedContent: true });
      textLayerTask = new pdfjs.TextLayer({
        container: textLayerDiv.current,
        textContentSource,
        viewport,
      });
      try {
        await textLayerTask.render();
      } catch (e) {
        if (!cancelled && e?.name !== 'RenderingCancelledException') {
          console.warn('PDF text layer render', e);
        }
        return;
      }
      if (cancelled || !textLayerDiv.current) {
        return;
      }
      const notifyScale = onScaleChangeRef.current;
      if (typeof notifyScale === 'function' && canvas.current) {
        const s = page.originalWidth / canvas.current.width;
        notifyScale(s);
      }
    })();

    return () => {
      cancelled = true;
      if (textLayerTask && typeof textLayerTask.cancel === 'function') {
        textLayerTask.cancel();
      }
    };
  }, [page, pageWidth]);

  useEffect(() => {
    if (canvasRef) {
      const int = setInterval(() => {
        if (
          canvasRef?.current?.style?.width !== canvas?.current?.style?.width ||
          canvasRef?.current?.style?.height !== canvas?.current?.style?.height
        ) {
          const annCanvas = canvasRef.current;
          annCanvas.width = canvas.current.width;
          annCanvas.height = canvas.current.height;
          annCanvas.style.width = canvas.current.style.width;
          annCanvas.style.height = canvas.current.style.height;
        }
      }, 500);
      return () => clearInterval(int);
    }
    return () => null;
  }, [file, canvasRef]);

  useEffect(() => {
    if (!data) {
      if (file instanceof Blob || file instanceof File) {
        setData(file);
      } else {
        getFile(file)
          .then(d => setData(d))
          .catch(e => {
            setLoaded(true);
            setError(e);
          });
      }
    }
  }, [data, file]);

  const handleStart = () => {
    setDragging(true);
  };

  const handleStop = (e, d) => {
    setDragging(false);
    const x = d.x + d.node.offsetLeft;
    const y = d.node.parentNode.offsetHeight - d.y - d.node.offsetTop - d.node.offsetHeight + 40;
    onSignaturePlaced({ x, y });
  };

  return (
    <Suspense fallback={loading}>
      {!error && data && (
        <Document file={data} className={docClassName} onLoadSuccess={loadPdf} loading={loading}>
          {loaded && (
            <Suspense fallback={loading}>
              {loaded && (
                <Box sx={pageWrapperSx} ref={observe}>
                  {canvasRef && <Box component="canvas" sx={annotationsSx} ref={canvasRef} />}
                  <Page
                    canvasRef={canvas}
                    width={pageWidth}
                    key={`page_${pageNum}`}
                    className={pageClassName}
                    pageNumber={pageNum}
                    onLoadSuccess={loadPage}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  >
                    <Box
                      ref={textLayerDiv}
                      className="textLayer"
                      sx={textLayerSx}
                      data-pdf-text-layer
                    />
                    {showSignature && (
                      <Draggable bounds="parent" onStart={handleStart} onStop={handleStop}>
                        <Box sx={getDraggableSx(draggableProps)}>
                          <Box sx={boxSx}>
                            <SvgIcon sx={iconSx}>
                              <SignRequestIcon />
                            </SvgIcon>
                            {I18n.t('AcknowledgementCreate.Signature')}
                          </Box>
                          <Box component="svg" viewBox="0 0 80 18" sx={signTextSx}>
                            <text x="0" y="7">
                              {I18n.t('AcknowledgementCreate.SignerName')}
                            </text>
                            <text x="0" y="15">
                              {I18n.t('AcknowledgementCreate.SigningDate')}
                            </text>
                          </Box>
                        </Box>
                      </Draggable>
                    )}
                  </Page>
                </Box>
              )}
            </Suspense>
          )}
        </Document>
      )}
    </Suspense>
  );
};

PDFPreviewLazy.propTypes = {
  file: any.isRequired,
  docClassName: string.isRequired,
  pageClassName: string.isRequired,
  loading: node.isRequired,
  width: number,
  onSignaturePlaced: func,
  showSignature: bool,
  canvasRef: any,
  onScaleChange: func,
  fullWidth: bool,
  pageNum: number,
};

export default PDFPreviewLazy;
